import React, { useState } from 'react';
import { Zap, CheckCircle, Clock, DollarSign, Users, ChevronRight } from 'lucide-react';

const OneClickRemediationPanel = ({ workflows, onExecute }) => {
  const [executing, setExecuting] = useState(null);

  const handleExecute = async (workflowId) => {
    setExecuting(workflowId);
    try {
      await onExecute(workflowId);
    } catch (error) {
      console.error('Remediation execution failed:', error);
    } finally {
      setExecuting(null);
    }
  };

  const getActionTypeLabel = (type) => {
    const labels = {
      backpay_calculation: 'Backpay Calculation',
      rate_correction: 'Rate Correction',
      leave_adjustment: 'Leave Adjustment',
      contract_amendment: 'Contract Amendment',
      policy_update: 'Policy Update',
      system_configuration: 'System Configuration'
    };
    return labels?.[type] || type;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors?.[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="space-y-4">
      {workflows?.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-50" />
          <p className="text-sm font-medium">No pending remediation actions</p>
          <p className="text-xs mt-1">All violations have been addressed</p>
        </div>
      ) : (
        workflows?.map((workflow, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            {/* Workflow Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-gray-900">{workflow?.workflowName}</h4>
                  {workflow?.isOneClick && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                      One-Click
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600">{workflow?.violation?.description}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium border ${
                getSeverityColor(workflow?.violation?.severity)
              }`}>
                {workflow?.violation?.severity?.toUpperCase()}
              </span>
            </div>

            {/* Workflow Metrics */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="flex items-center gap-2 text-xs">
                <Users className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">{workflow?.affectedEmployeeCount} affected</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">~{workflow?.estimatedTimeMinutes}min</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">K{workflow?.estimatedCostKina?.toLocaleString()}</span>
              </div>
            </div>

            {/* Execution Steps Preview */}
            <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
              <div className="font-medium text-gray-700 mb-1">Execution Steps:</div>
              <div className="space-y-1">
                {workflow?.executionSteps?.slice(0, 3)?.map((step, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-600">
                    <ChevronRight className="h-3 w-3" />
                    <span>{step?.action || step?.step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Execute Button */}
            <button
              onClick={() => handleExecute(workflow?.id)}
              disabled={executing === workflow?.id}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {executing === workflow?.id ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Executing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Execute Remediation
                </>
              )}
            </button>
          </div>
        ))
      )}
    </div>
  );
};

export default OneClickRemediationPanel;